import { Link } from '@tanstack/react-router';
import { useTranslation } from '@/context/TranslationContext';
import { SEO, generateBreadcrumbSchema } from '@/components/shared/seo';
import styles from '../legal/legal.module.scss';

export function Privacy() {
    const { t, language } = useTranslation();

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const monthName = t(`PRIVACY.MONTH.${currentMonth}`);

    const breadcrumbSchema = generateBreadcrumbSchema([
        { name: language === 'de' ? 'Startseite' : 'Home', url: '/' },
        { name: t('PRIVACY.TITLE'), url: language === 'de' ? '/datenschutz' : '/privacy' },
    ]);

    return (
        <main className={styles.legalPage}>
            <SEO
                title={t('PRIVACY.TITLE')}
                description={language === 'de'
                    ? 'Datenschutzerklärung von KoriCode - Informationen zum Umgang mit Ihren personenbezogenen Daten'
                    : 'Privacy policy of KoriCode - Information about how we handle your personal data'}
                canonicalUrl={language === 'de' ? '/datenschutz' : '/privacy'}
                language={language}
                noIndex={false}
                structuredData={breadcrumbSchema}
            />
            <div className={styles.container}>
                <Link to="/" className={styles.backLink}>
                    <i className='bx bx-arrow-back'></i> {t('NAV.HOME')}
                </Link>

                <h1>{t('PRIVACY.TITLE')}</h1>

                <section className={styles.section}>
                    <h2>1. {t('PRIVACY.SECTION1.TITLE')}</h2>
                    <h3>{t('PRIVACY.SECTION1.SUBTITLE')}</h3>
                    <p>
                        {t('PRIVACY.SECTION1.TEXT')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>2. {t('PRIVACY.SECTION2.TITLE')}</h2>
                    <h3>{t('PRIVACY.SECTION2.Q1')}</h3>
                    <p>
                        {t('PRIVACY.SECTION2.A1')}
                    </p>

                    <h3>{t('PRIVACY.SECTION2.Q2')}</h3>
                    <p>
                        {t('PRIVACY.SECTION2.A2.P1')}
                    </p>
                    <p>
                        {t('PRIVACY.SECTION2.A2.P2')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>3. {t('PRIVACY.SECTION3.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION3.P1')}
                    </p>
                    <p>
                        {t('PRIVACY.SECTION3.P2')}
                    </p>
                    <ul>
                        <li><strong>{t('PRIVACY.SECTION3.NECESSARY')}</strong> {t('PRIVACY.SECTION3.NECESSARY.DESC')}</li>
                        <li><strong>{t('PRIVACY.SECTION3.ANALYTICAL')}</strong> {t('PRIVACY.SECTION3.ANALYTICAL.DESC')}</li>
                    </ul>
                    <p>
                        {t('PRIVACY.SECTION3.P3')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>4. {t('PRIVACY.SECTION4.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION4.TEXT')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>5. {t('PRIVACY.SECTION5.TITLE')}</h2>
                    <p>{t('PRIVACY.SECTION5.INTRO')}</p>
                    <ul>
                        <li>{t('PRIVACY.SECTION5.RIGHT1')}</li>
                        <li>{t('PRIVACY.SECTION5.RIGHT2')}</li>
                        <li>{t('PRIVACY.SECTION5.RIGHT3')}</li>
                        <li>{t('PRIVACY.SECTION5.RIGHT4')}</li>
                        <li>{t('PRIVACY.SECTION5.RIGHT5')}</li>
                        <li>{t('PRIVACY.SECTION5.RIGHT6')}</li>
                    </ul>
                    <p>
                        {t('PRIVACY.SECTION5.CONTACT')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>6. {t('PRIVACY.SECTION6.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION6.TEXT1')}
                    </p>
                    <p>
                        {t('PRIVACY.SECTION6.TEXT2')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>7. {t('PRIVACY.SECTION7.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION7.TEXT')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>8. {t('PRIVACY.SECTION8.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION8.TEXT1')}
                    </p>
                    <ul>
                        <li>{t('PRIVACY.SECTION8.LIST1')}</li>
                        <li>{t('PRIVACY.SECTION8.LIST2')}</li>
                        <li>{t('PRIVACY.SECTION8.LIST3')}</li>
                        <li>{t('PRIVACY.SECTION8.LIST4')}</li>
                        <li>{t('PRIVACY.SECTION8.LIST5')}</li>
                        <li>{t('PRIVACY.SECTION8.LIST6')}</li>
                        <li>{t('PRIVACY.SECTION8.LIST7')}</li>
                        <li>{t('PRIVACY.SECTION8.LIST8')}</li>
                    </ul>
                    <p>
                        {t('PRIVACY.SECTION8.TEXT2')}
                    </p>
                    <p>
                        {t('PRIVACY.SECTION8.TEXT3')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>9. {t('PRIVACY.SECTION9.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION9.TEXT1')}
                    </p>
                    <p>
                        {t('PRIVACY.SECTION9.TEXT2')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>10. {t('PRIVACY.SECTION10.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION10.TEXT1')}
                    </p>
                    <p>
                        {t('PRIVACY.SECTION10.TEXT2')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>11. {t('PRIVACY.SECTION11.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION11.TEXT1')}
                    </p>
                    <p>
                        {t('PRIVACY.SECTION11.TEXT2')}{' '}
                        <a href={t('PRIVACY.SECTION11.LINK')} target="_blank" rel="noopener noreferrer">
                            {t('PRIVACY.SECTION11.LINK')}
                        </a>.
                    </p>
                    <p>
                        {t('PRIVACY.SECTION11.TEXT3')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>12. {t('PRIVACY.SECTION12.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION12.TEXT')}
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>13. {t('PRIVACY.SECTION13.TITLE')}</h2>
                    <p>
                        {t('PRIVACY.SECTION13.TEXT')}
                    </p>
                </section>

                <section className={styles.section}>
                    <p className={styles.updated}>
                        {t('PRIVACY.STATUS')}: {monthName} {currentYear}
                    </p>
                </section>
            </div>
        </main>
    );
}
